import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { ReferenceField, TextField, useTranslate } from 'react-admin';

const useStyles = makeStyles((theme) => {
  return {
    wrapper: {
      paddingLeft: theme.spacing(1),
    },
    card: {
      width: '100%',
    },
    label: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    value: {
      marginTop: theme.spacing(0.5),
      fontSize: '0.95rem',
      minHeight: '1.5rem',
    },
    empty: {
      fontStyle: 'italic',
      color: theme.palette.text.disabled,
    },
    message: {
      padding: theme.spacing(2),
    },
    error: {
      padding: theme.spacing(2),
      color: theme.palette.error.main,
    },
  };
});

const MANAGER_SLOTS = [
  { source: 'area_manager', labelKey: 'area_manager' },
  { source: 'backup_area_manager', labelKey: 'backup_area_manager' },
  { source: 'account_manager', labelKey: 'account_manager' },
  { source: 'backup_account_manager', labelKey: 'backup_account_manager' },
  { source: 'quality_manager', labelKey: 'quality_manager' },
  { source: 'backup_quality_manager', labelKey: 'backup_quality_manager' },
];

const ManagerSlot = ({ record, source, labelKey }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const sfid = record ? record[source] : null;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography className={classes.label}>
        {translate(`resources.account_operational_info.fields.${labelKey}`)}
      </Typography>
      <div className={classes.value}>
        {sfid ? (
          <ReferenceField
            record={record}
            source={source}
            reference="cleaner"
            basePath="/cleaner"
            link="show"
            allowEmpty
          >
            <TextField source="fullname" />
          </ReferenceField>
        ) : (
          <Typography component="span" className={classes.empty}>
            —
          </Typography>
        )}
      </div>
    </Grid>
  );
};

ManagerSlot.propTypes = {
  record: PropTypes.object,
  source: PropTypes.string.isRequired,
  labelKey: PropTypes.string.isRequired,
};

ManagerSlot.defaultProps = {
  record: undefined,
};

const OperationalInfoCard = ({ record, isLoading, error }) => {
  const classes = useStyles();
  const translate = useTranslate();

  const renderBody = () => {
    if (isLoading) {
      return (
        <Typography className={classes.message}>
          {translate('ra.page.loading')}
        </Typography>
      );
    }
    if (error) {
      return (
        <Typography className={classes.error}>
          {translate('resources.account_operational_info.error', {
            _: 'Erreur lors du chargement',
          })}{' '}
          : {error.message}
        </Typography>
      );
    }
    if (!record) {
      return (
        <Typography className={classes.message}>
          {translate('resources.account_operational_info.empty')}
        </Typography>
      );
    }

    return (
      <Grid container spacing={3}>
        {MANAGER_SLOTS.map((slot) => {
          return <ManagerSlot key={slot.source} record={record} {...slot} />;
        })}
      </Grid>
    );
  };

  return (
    <Grid item xs={12} className={classes.wrapper}>
      <Card className={classes.card}>
        <CardHeader
          title={translate('resources.account.card_title.operational')}
        />
        <CardContent>{renderBody()}</CardContent>
      </Card>
    </Grid>
  );
};

OperationalInfoCard.propTypes = {
  record: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.instanceOf(Error),
};

OperationalInfoCard.defaultProps = {
  record: null,
  isLoading: false,
  error: null,
};

export default OperationalInfoCard;
